import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AccountCart from "../components/account-cart";
import Container from "../components/basicComponents/container";
import {
  setDepartmentsData,
  setUsersData,
  setRolesData,
  setGroupingData,
  setArticlesData,
  setNewsData,
  setProjectsData,
  setFaqData,
} from "../state-management/actions/global-state-actions";
import { getDepartments } from "../state-management/actions/department-actions";
import { userGetAll } from "../state-management/actions/user-actions";
import { getArticles } from "../state-management/actions/article-actions";
import { getNews } from "../state-management/actions/news-actions";
import { getProjects } from "../state-management/actions/project-actions";
import { getRoles } from "../state-management/actions/role-actions";
import { getAllGrouping } from "../state-management/actions/grouping-actions";
import { getFaqs } from "../state-management/actions/faq-actions";
import { selectGlobalState } from "../state-management/selectors";
import apiChart from "../components/charts/api-chart";
import theme from "../config/theme";
import CurrentTimeAndDate from "../components/basicComponents/currentTimeAndDate";
import "../components/charts/register-chart";
import IRANSans from "../assets/fonts/IRANSans.ttf"; // Import the font file
import AreaChartComponent from "../components/charts/area-chart";
import RadarChart from "../components/charts/radar-chart";
import StackedBarChart from "../components/charts/stacked-bar-chart";
import StackedDonutCharts from "../components/charts/users-publish-chart";
import DepartmentActivities from "../components/department-activities";
import activeitesQuantity from "../utils/activitiesQuantity";
import DepartmentProgress from "../components/charts/departmant-views";
import { calculateDepartmentViews } from "../utils/departmantViewData";
import LeaderBoard from "../components/leader-board";
import leaderBoardList from "../utils/leaderBoardList";

export default function Dashboard() {
  const dispatch = useDispatch();
  const { departmentsData, articlesData, newsData } =
    useSelector(selectGlobalState);
  const themeMode = useSelector((state: any) => state.globalState.theme);
  const mode = themeMode === "light" ? theme.light : theme.dark;
  const lengthData = apiChart();
  const departmentActivitiesData = activeitesQuantity();
  const newsViewData = calculateDepartmentViews(departmentsData, newsData);
  const articleViewData = calculateDepartmentViews(
    departmentsData,
    articlesData
  );
  const [leaderBoardNumber, setLeaderBoardNumber] = useState<number>(14);

  const leaderBoardData = leaderBoardList(leaderBoardNumber);

  const fetchData = async () => {
    try {
      const responses = await Promise.allSettled([
        userGetAll()(dispatch),
        getDepartments()(dispatch),
        getRoles()(dispatch),
        getAllGrouping()(dispatch),
        getArticles()(dispatch),
        getNews()(dispatch),
        getProjects()(dispatch),
        getFaqs()(dispatch),
      ]);

      responses.forEach((response, index) => {
        if (response.status === "fulfilled") {
          switch (index) {
            case 0:
              dispatch(setUsersData(response.value?.data));
              break;
            case 1:
              dispatch(setDepartmentsData(response.value?.data));
              break;
            case 2: {
              let roles = response.value?.data;
              if (!Array.isArray(roles)) {
                roles = roles.roles || [];
              }
              dispatch(setRolesData(roles));
              break;
            }
            case 3:
              dispatch(setGroupingData(response.value?.data));
              break;
            case 4:
              dispatch(setArticlesData(response.value?.data));
              break;
            case 5:
              dispatch(setNewsData(response.value?.data));
              break;
            case 6:
              dispatch(setProjectsData(response.value?.data));
              break;
            case 7:
              dispatch(setFaqData(response.value?.data));
              break;
          }
        } else {
          console.error(
            `Error fetching data for API ${index}:`,
            response.reason
          );
        }
      });
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  useEffect(() => {
    fetchData();
    const style = document.createElement("style");
    style.innerHTML = `
      @font-face {
        font-family: 'IRANSans';
        src: url(${IRANSans}) format('truetype');
        font-weight: normal;
        font-style: normal;
      }
      body {
        font-family: 'IRANSans', sans-serif;
      }
    `;
    document.head.appendChild(style);
  }, [dispatch]);

  if (!departmentsData || !articlesData) {
    return <div>Loading...</div>;
  }

  return (
    <div className={`flex flex-col items-center w-full ${mode}`}>
      <Container header="">
        <div className="w-full grid grid-cols-1 md:grid-cols-4 md:gap-5 gap-2">
          <div className="col-span-2 md:col-span-3 flex flex-col md:gap-y-5 gap-y-1">
            <AreaChartComponent />
            <div className="border border-[#d4d4d4] rounded-lg p-1 md:p-3 md:max-h-[230px] max-h-[250px] overflow-y-auto">
              {departmentActivitiesData
                .sort(
                  (a: any, b: any) =>
                    b.activityPercentage - a.activityPercentage
                )
                .map((item: any) => {
                  return (
                    <DepartmentActivities
                      departmentDetailes={item.departmentDetailes}
                      departmentUsers={item.departmentUsers}
                      activityPercentage={item.activityPercentage}
                      articleQuantity={item.articleQuantity}
                      projectQuantity={item.projectQuantity}
                      newsQuantity={item.newsQuantity}
                    />
                  );
                })}
            </div>
            <div className=" flex md:flex-row flex-col md:gap-y-5 gap-y-2 w-full gap-x-4">
              <div className="border border-[#AAAAAA] rounded-lg md:w-3/5 w-full ">
                <StackedDonutCharts />
              </div>
              <div className="border border-[#AAAAAA] rounded-lg md:w-2/5 w-full">
                <StackedBarChart />
              </div>
            </div>
            <div className="w-full flex gap-y-4">
              <div className="flex md:flex-row flex-col md:gap-y-0 md:gap-y-5 gap-y-2 justify-between w-full gap-x-5 ">
                <DepartmentProgress
                  data={articleViewData}
                  type="بازدید مقالات"
                />
                <DepartmentProgress data={newsViewData} type="بازدید اخبار" />
              </div>
            </div>
          </div>

          <div className="col-span-2 md:col-span-1 flex flex-col md:gap-y-5 gap-y-2 mt-2.5">
            <div className="flex flex-col  items-end md:gap-y-4 gap-y-2 w-full">
              {lengthData.map((item: any, index: any) => (
                <AccountCart
                  key={index}
                  active={item.active}
                  deactive={item.deactive}
                  name={item.name}
                  Icon={item.icon}
                />
              ))}
            </div>
            <div className="w-full border border-[#AAAAAA] rounded-lg px-2 ">
              <LeaderBoard
                users={leaderBoardData.sort(
                  (a: any, b: any) => b.score - a.score
                )}
                handler={setLeaderBoardNumber}
              />
            </div>
            <div className="w-full border border-[#AAAAAA] rounded-lg py-2">
              <RadarChart />
            </div>
            <div className="w-full">
              <CurrentTimeAndDate />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
